import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Copy, Archive, ArchiveRestore, Star,
  Layers, Users, Clock, MoreHorizontal, Sparkles, X, FileText,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { filterVisibleTemplates } from "@/lib/workflowFilters";

const STATUS_META = {
  active:   { label: "Actif",   bg: "bg-[#DCFCE7]", text: "text-[#166534]", dot: "#22C55E" },
  inactive: { label: "Inactif", bg: "bg-[#F3F4F6]", text: "text-[#6B7280]", dot: "#9CA3AF" },
  archived: { label: "Archivé", bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", dot: "#EF4444" },
};

const formatFrDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return "—";
  }
};

const CreateMenu = ({ open, onClose, onBlank, onDuplicate, templates }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid="workflow-create-menu">
      <div onClick={onClose} className="absolute inset-0 bg-[#111111]/25 backdrop-blur-[2px]" />
      <aside
        className="absolute top-0 right-0 h-full w-full sm:w-[440px] bg-white flex flex-col animate-[slide-in-right_.3s_cubic-bezier(0.22,1,0.36,1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[#EEF2F5] flex items-start justify-between">
          <div>
            <p className="text-[11px] font-semibold text-[#22C55E] uppercase tracking-wider">
              Nouveau workflow
            </p>
            <h3 className="mt-2 text-[20px] font-semibold text-[#111111] tracking-tight">
              Comment démarrer ?
            </h3>
            <p className="mt-1 text-[12.5px] text-[#6B7280]">
              Deux façons de créer un workflow personnalisé.
            </p>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center">
            <X size={16} strokeWidth={2} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          <button
            data-testid="create-workflow-blank"
            onClick={onBlank}
            className="w-full text-left rounded-2xl border border-[#EEF2F5] hover:border-[#111111] bg-white p-4 transition-colors group"
          >
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-2xl bg-[#F7F8FA] group-hover:bg-[#C8FF2F] flex items-center justify-center text-[#111111] transition-colors">
                <Sparkles size={16} strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold text-[#111111]">Partir de zéro</p>
                <p className="mt-0.5 text-[12.5px] text-[#6B7280] leading-relaxed">
                  Une étape de départ, à toi de construire le reste.
                </p>
              </div>
            </div>
          </button>

          <p className="mt-4 text-[10.5px] font-semibold text-[#6B7280] uppercase tracking-wider">
            Dupliquer un modèle
          </p>
          {templates.map((t) => (
            <button
              key={t.id}
              data-testid={`duplicate-source-${t.slug}`}
              onClick={() => onDuplicate(t)}
              className="w-full text-left rounded-2xl border border-[#EEF2F5] hover:border-[#111111] bg-white p-4 transition-colors group"
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-2xl bg-[#F7F8FA] group-hover:bg-[#C8FF2F] flex items-center justify-center text-[#111111] transition-colors">
                  <Copy size={16} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold text-[#111111] truncate">
                    {t.name}
                  </p>
                  <p className="mt-0.5 text-[12.5px] text-[#6B7280] leading-relaxed line-clamp-2">
                    {t.description}
                  </p>
                  <p className="mt-1 text-[11px] text-[#9CA3AF]">
                    {t.steps?.length || 0} étapes · {t.is_system ? "Modèle wyvio" : "Personnalisé"}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </aside>
    </div>
  );
};

const NewNameDialog = ({ open, title, onCancel, onConfirm, defaultValue = "" }) => {
  const [name, setName] = useState(defaultValue);
  useEffect(() => { if (open) setName(defaultValue); }, [open, defaultValue]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60]" data-testid="new-name-dialog">
      <div onClick={onCancel} className="absolute inset-0 bg-[#111111]/40 backdrop-blur-[2px]" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-[420px] rounded-3xl bg-white p-6 shadow-[0_20px_60px_-20px_rgba(17,17,17,0.35)] animate-[fade-in_.2s_ease-out]">
          <h3 className="text-[18px] font-semibold text-[#111111] tracking-tight">{title}</h3>
          <label className="block mt-4">
            <span className="text-[11.5px] font-semibold text-[#6B7280] uppercase tracking-wider">Nom du workflow</span>
            <input
              data-testid="new-name-input"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && name.trim() && onConfirm(name.trim())}
              placeholder="Ex : Vente directe premium"
              className="mt-2 w-full h-11 rounded-2xl border border-[#EEF2F5] bg-white px-4 text-[14px] outline-none focus:border-[#111111]"
            />
          </label>
          <div className="mt-6 flex items-center justify-end gap-2">
            <button
              data-testid="new-name-cancel"
              onClick={onCancel}
              className="h-11 rounded-full bg-white border border-[#EEF2F5] text-[13px] font-semibold text-[#111111] px-4"
            >
              Annuler
            </button>
            <button
              data-testid="new-name-confirm"
              onClick={() => name.trim() && onConfirm(name.trim())}
              disabled={!name.trim()}
              className="h-11 rounded-full bg-[#111111] text-white text-[13px] font-semibold px-5 disabled:opacity-50"
            >
              Créer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const WorkflowCard = ({ tpl, onOpen, onArchive, onActivate, onDuplicate, onSetDefault, isAdmin }) => {
  const status = STATUS_META[tpl.status] || STATUS_META.active;
  return (
    <article
      data-testid={`workflow-card-${tpl.slug}`}
      className="wyvio-card wyvio-card-hover p-6 group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${status.bg} ${status.text}`}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: status.dot }} />
              {status.label}
            </span>
            {tpl.is_default && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#C8FF2F] text-[#111111] px-2 py-0.5 text-[10px] font-semibold">
                <Star size={10} strokeWidth={2.5} /> Par défaut
              </span>
            )}
            {tpl.is_system && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#111111] text-white px-2 py-0.5 text-[10px] font-semibold">
                wyvio
              </span>
            )}
          </div>
          <h4 className="mt-3 text-[18px] font-semibold text-[#111111] tracking-tight truncate">
            {tpl.name}
          </h4>
          <p className="mt-1 text-[12.5px] text-[#6B7280] leading-relaxed line-clamp-2">
            {tpl.description || "Workflow personnalisé."}
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3">
        <div>
          <p className="text-[10.5px] font-semibold text-[#6B7280] uppercase tracking-wider">Étapes</p>
          <p className="mt-1 text-[16px] font-semibold text-[#111111] tracking-tight inline-flex items-center gap-1.5">
            <Layers size={13} strokeWidth={2} />
            {tpl.steps?.length || 0}
          </p>
        </div>
        <div>
          <p className="text-[10.5px] font-semibold text-[#6B7280] uppercase tracking-wider">Actifs</p>
          <p className="mt-1 text-[16px] font-semibold text-[#111111] tracking-tight inline-flex items-center gap-1.5">
            <Users size={13} strokeWidth={2} />
            {tpl.active_dossiers_count || 0}
          </p>
        </div>
        <div>
          <p className="text-[10.5px] font-semibold text-[#6B7280] uppercase tracking-wider">Version</p>
          <p className="mt-1 text-[16px] font-semibold text-[#111111] tracking-tight inline-flex items-center gap-1.5">
            <Clock size={13} strokeWidth={2} />
            v{tpl.version || 1}
          </p>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-[#EEF2F5] flex items-center justify-between gap-2">
        <p className="text-[11px] text-[#9CA3AF]">
          Modifié le {formatFrDate(tpl.updated_at)}
        </p>
        <div className="flex items-center gap-1.5">
          {isAdmin && (
            <>
              <button
                data-testid={`workflow-duplicate-${tpl.slug}`}
                onClick={() => onDuplicate(tpl)}
                title="Dupliquer"
                className="h-8 w-8 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center text-[#6B7280] hover:text-[#111111] transition-colors"
              >
                <Copy size={13} strokeWidth={2} />
              </button>
              {tpl.status === "archived" ? (
                <button
                  data-testid={`workflow-activate-${tpl.slug}`}
                  onClick={() => onActivate(tpl)}
                  title="Réactiver"
                  className="h-8 w-8 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center text-[#6B7280] hover:text-[#111111] transition-colors"
                >
                  <ArchiveRestore size={13} strokeWidth={2} />
                </button>
              ) : !tpl.is_system && !tpl.is_default ? (
                <button
                  data-testid={`workflow-archive-${tpl.slug}`}
                  onClick={() => onArchive(tpl)}
                  title="Archiver"
                  className="h-8 w-8 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center text-[#6B7280] hover:text-[#991B1B] transition-colors"
                >
                  <Archive size={13} strokeWidth={2} />
                </button>
              ) : null}
              {!tpl.is_default && tpl.status === "active" && (
                <button
                  data-testid={`workflow-set-default-${tpl.slug}`}
                  onClick={() => onSetDefault(tpl)}
                  title="Définir par défaut"
                  className="h-8 w-8 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center text-[#6B7280] hover:text-[#111111] transition-colors"
                >
                  <Star size={13} strokeWidth={2} />
                </button>
              )}
            </>
          )}
          <button
            data-testid={`workflow-open-${tpl.slug}`}
            onClick={() => onOpen(tpl)}
            className="h-9 rounded-full bg-[#111111] text-white text-[12.5px] font-semibold px-4 hover:bg-black inline-flex items-center gap-1.5 transition-colors"
          >
            Ouvrir
          </button>
        </div>
      </div>
    </article>
  );
};

const WorkflowList = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const isAdmin = ["admin", "agency_owner", "platform_admin"].includes(user?.role);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [nameDialog, setNameDialog] = useState(null); // {mode:"blank"|"duplicate", source?}
  const [archiveConfirm, setArchiveConfirm] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await api.listWorkflowTemplates();
      const visible = filterVisibleTemplates(list);
      setTemplates(visible);
      setError(null);
      // Redirection auto : si un seul workflow actif est visible, ouvre-le.
      const active = visible.filter((t) => t.status === "active");
      if (active.length === 1) {
        navigate(`/app/parametres/workflows/${active[0].id}`, { replace: true });
        return;
      }
    } catch (e) {
      setError(e.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const handleCreateBlank = () => {
    setCreateOpen(false);
    setNameDialog({ mode: "blank" });
  };
  const handleDuplicate = (source) => {
    setCreateOpen(false);
    setNameDialog({ mode: "duplicate", source });
  };
  const handleConfirmName = async (name) => {
    const mode = nameDialog.mode;
    const source = nameDialog.source;
    setNameDialog(null);
    try {
      let created;
      if (mode === "blank") {
        created = await api.createBlankWorkflow(name);
      } else {
        created = await api.duplicateWorkflowTemplate(source.id, name);
      }
      await refresh();
      navigate(`/app/parametres/workflows/${created.id}`);
    } catch (e) {
      toast.error(e.message || "Erreur");
    }
  };

  const handleArchive = (tpl) => setArchiveConfirm(tpl);
  const confirmArchive = async () => {
    const tpl = archiveConfirm;
    setArchiveConfirm(null);
    try {
      await api.archiveWorkflowTemplate(tpl.id);
      await refresh();
    } catch (e) { toast.error(e.message || "Erreur"); }
  };
  const handleActivate = async (tpl) => {
    try { await api.activateWorkflowTemplate(tpl.id); await refresh(); }
    catch (e) { toast.error(e.message || "Erreur"); }
  };
  const handleDuplicateExisting = (tpl) => setNameDialog({ mode: "duplicate", source: tpl });
  const handleSetDefault = async (tpl) => {
    try { await api.updateWorkflowTemplate(tpl.id, { is_default: true }); await refresh(); }
    catch (e) { toast.error(e.message || "Erreur"); }
  };

  return (
    <div className="wyvio-fade-in" data-testid="page-workflows">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <button
            onClick={() => navigate("/app/parametres")}
            data-testid="workflows-back"
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#6B7280] hover:text-[#111111] transition-colors"
          >
            <ArrowLeft size={13} strokeWidth={2} /> Paramètres
          </button>
          <h1 className="mt-1.5 text-[32px] leading-tight font-semibold tracking-tight text-[#111111]">
            Workflows
          </h1>
          <p className="mt-2 text-[14px] text-[#6B7280] max-w-xl">
            Adapte wyvio à ton processus de vente : chaque workflow définit les étapes, les documents à collecter et les règles de blocage de livraison.
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid="new-workflow-btn"
            onClick={() => setCreateOpen(true)}
            className="h-11 rounded-full bg-[#111111] text-white text-[13px] font-semibold px-5 inline-flex items-center gap-2 hover:bg-black transition-colors self-start lg:self-auto"
          >
            <Plus size={14} strokeWidth={2.25} />
            Nouveau workflow
          </button>
        )}
      </header>

      {error && (
        <div className="mt-6 rounded-2xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-[13px] text-[#991B1B]">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-8 text-[13px] text-[#6B7280]">Chargement…</div>
      ) : templates.length === 0 ? (
        <div className="mt-10 rounded-3xl border border-dashed border-[#EEF2F5] p-10 text-center">
          <FileText size={22} strokeWidth={1.5} className="mx-auto text-[#9CA3AF]" />
          <p className="mt-3 text-[15px] font-semibold text-[#111111]">Aucun workflow encore.</p>
          <p className="mt-1 text-[13px] text-[#6B7280]">Crée ton premier workflow pour démarrer.</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="workflows-grid">
          {templates.map((tpl) => (
            <WorkflowCard
              key={tpl.id}
              tpl={tpl}
              isAdmin={isAdmin}
              onOpen={(t) => navigate(`/app/parametres/workflows/${t.id}`)}
              onArchive={handleArchive}
              onActivate={handleActivate}
              onDuplicate={handleDuplicateExisting}
              onSetDefault={handleSetDefault}
            />
          ))}
        </div>
      )}

      <CreateMenu
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onBlank={handleCreateBlank}
        onDuplicate={handleDuplicate}
        templates={templates.filter((t) => t.status !== "archived")}
      />

      <NewNameDialog
        open={!!nameDialog}
        title={nameDialog?.mode === "blank" ? "Nouveau workflow vierge" : `Dupliquer « ${nameDialog?.source?.name || ""} »`}
        defaultValue={nameDialog?.mode === "duplicate" ? `${nameDialog.source.name} (copie)` : ""}
        onCancel={() => setNameDialog(null)}
        onConfirm={handleConfirmName}
      />

      {archiveConfirm && (
        <div className="fixed inset-0 z-[60]" data-testid="archive-confirm">
          <div onClick={() => setArchiveConfirm(null)} className="absolute inset-0 bg-[#111111]/40" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-[440px] rounded-3xl bg-white p-6 shadow-[0_20px_60px_-20px_rgba(17,17,17,0.35)]">
              <h3 className="text-[18px] font-semibold text-[#111111] tracking-tight">
                Archiver « {archiveConfirm.name} » ?
              </h3>
              <p className="mt-2 text-[13.5px] text-[#6B7280] leading-relaxed">
                Les dossiers en cours qui utilisent ce workflow ne seront <span className="font-semibold text-[#111111]">pas affectés</span> — ils gardent leur configuration.
                Aucun nouveau dossier ne pourra utiliser ce workflow.
              </p>
              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  data-testid="archive-cancel"
                  onClick={() => setArchiveConfirm(null)}
                  className="h-11 rounded-full bg-white border border-[#EEF2F5] text-[13px] font-semibold text-[#111111] px-4"
                >
                  Annuler
                </button>
                <button
                  data-testid="archive-confirm-btn"
                  onClick={confirmArchive}
                  className="h-11 rounded-full bg-[#991B1B] text-white text-[13px] font-semibold px-5 hover:bg-[#7F1D1D]"
                >
                  Archiver
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowList;
